import { isSSR } from '@atlaskit/editor-common/core-utils';
import type {
	BatchFetchConfig,
	BlockNodeIdentifiers,
	SyncBlockInstance,
} from '@atlaskit/editor-synced-block-provider/providers/types';
import { batchFetchData } from '@atlaskit/editor-synced-block-provider/providers/block-service/blockServiceAPI';
import { getConfluencePageAri } from '@atlaskit/editor-synced-block-provider/clients/confluence/ari';
import { parseResourceId } from '@atlaskit/editor-synced-block-provider/utils/resourceId';
import { SyncBlockError } from '@atlaskit/editor-synced-block-provider/common/types';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import {
	SyncedBlocksSSRErrorCodeMap,
	type SyncedBlocksSSRErrorMetadata,
} from '@atlaskit/editor-common/sync-block';

import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import {
	preloadJiraMediaToken,
	preloadMediaToken,
} from '@confluence/fabric-media-support/entry-points/preloadMediaToken.ts';
import { expVal } from '@confluence/feature-experiments';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';

import { addToSSREditorNodeDataProvidersStore } from './utils/ssrDataStore.ts';
import { syncBlockContainsNodes } from './utils/syncBlockContainsNodes.ts';
import { extractSyncBlockIdentifiers } from './utils/extractSyncBlockIdentifiers.ts';

const MEASURE_SEGMENT = 'ssr-app/prepare/preloadQuery/syncedBlocksData';

// Timeout for fetching synced block references in milliseconds
// This is currently set to higher value to account for potential network latencys
// We will monitor performance and adjust this value as needed
const DEFAULT_TIMEOUT = 1500;

// New default timeout for batch fetch implementation, which includes more relistics value
const DEFAULT_TIMEOUT_NEXT = 500;

export interface PreloadSyncedBlocksDataOptions {
	/**
	 * The ID of the content to preload synced blocks for
	 */
	contentId: string;

	/**
	 * The cloud ID associated with the content
	 */
	cloudId: string;

	/**
	 * Whether the content is a blog post
	 */
	isBlog: boolean;

	/**
	 * The ADF document containing synced block nodes
	 * Used for bulk fetching (batchFetchData) with block identifiers extracted from the document
	 */
	doc?: JSONDocNode;
	/**
	 * The measure segment identifier for performance tracking
	 */
	measureSegment?: string;

	/**
	 * Timeout in milliseconds for fetching synced block references
	 * @default 1500
	 */
	timeout?: number;
}

/**
 * Preloads synced blocks data and stores it in the window object for hydration.
 *
 * This function handles a special case of editor node (syncBlock) data providers that requires
 * separate preloading due to its nested structure and performance characteristics.
 *
 * **How it works**
 *
 * This function is called after the main ADF document has been fetched. It parses the ADF
 * document to extract synced block identifiers (resourceId + localId). If synced blocks are
 * found, it batch-fetches their data using `batchFetchData` with a configurable timeout.
 * If no synced blocks are present in the document, the function returns early without
 * making any network requests.
 *
 * Once synced block data is fetched, it is stored in the window object via
 * `addToSSREditorNodeDataProvidersStore` for client-side hydration. Additionally, media
 * tokens are preloaded for any synced blocks that contain media nodes.
 *
 * **Feature flag encapsulation**
 *
 * All feature flag checks and conditional logic for synced blocks are centralized
 * here rather than scattered across multiple files.
 *
 * @param options - Configuration options for preloading synced blocks
 * @param options.contentId - The ID of the content to preload synced blocks for
 * @param options.cloudId - The cloud ID associated with the content
 * @param options.isBlog - Whether the content is a blog post
 * @param options.doc - The ADF document to parse for synced block identifiers
 * @param options.measureSegment - Optional segment name for performance tracking
 * @param options.timeout - Optional timeout in milliseconds for fetching (default: 1500ms)
 * @returns Promise that resolves when preloading is complete
 */
export async function preloadSyncedBlocksData(
	options: PreloadSyncedBlocksDataOptions,
): Promise<void> {
	const { contentId, cloudId, isBlog, doc, measureSegment, timeout } = options;
	// Check feature flags before attempting to fetch synced block data

	if (
		!contentId ||
		!cloudId ||
		!doc ||
		!isSSR() ||
		typeof isBlog === 'undefined' ||
		!expVal('platform_synced_block', 'isEnabled', false)
	) {
		if (
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			expVal('platform_synced_block', 'isEnabled', false)
		) {
			ssrPrepareErrorsClient.submitError(
				new Error('Skipped synced blocks preload: guard condition failed'),
				{
					isSsr: isSSR(),
					contentId,
					cloudId,
					isBlog,
				},
			);
		}

		return;
	}

	const segmentName = measureSegment || MEASURE_SEGMENT;
	let blockNodeIdentifiers: BlockNodeIdentifiers[] = [];

	// When FG is enabled, check for empty synced blocks BEFORE entering the measured run
	// so pages with no synced blocks don't record a metric for this segment
	blockNodeIdentifiers = extractSyncBlockIdentifiers(doc);
	if (blockNodeIdentifiers.length === 0) {
		return;
	}

	if (doc && expVal('platform_editor_sync_block_ssr_config', 'isEnabled', false)) {
		blockNodeIdentifiers = extractSyncBlockIdentifiers(doc);

		// If no synced blocks found, return early
		if (blockNodeIdentifiers.length === 0) {
			return;
		}
	}

	await SSRMeasures.run(segmentName, async () => {
		// Use bulk load (batchFetchData) when doc is available
		if (doc) {
			// Extract block identifiers from the ADF document
			if (!expVal('platform_editor_sync_block_ssr_config', 'isEnabled', false)) {
				blockNodeIdentifiers = extractSyncBlockIdentifiers(doc);
			}
		}


		const syncBlockReferences = await fetchSyncBlockReferencesBulk(
			contentId,
			cloudId,
			isBlog,
			blockNodeIdentifiers,
			segmentName,
			timeout,
		);

		if (!syncBlockReferences) {
			if (blockNodeIdentifiers.length > 0) {
				ssrPrepareErrorsClient.submitError(
					new Error(
						`Synced block fetch returned incomplete results: ${blockNodeIdentifiers.length} blocks missing`,
					),
					{
						code: SyncedBlocksSSRErrorCodeMap.FETCH_INCOMPLETE,
						contentId,
						cloudId,
						isBlog,
						identifiers: blockNodeIdentifiers,
						missingIdentifiers: blockNodeIdentifiers,
					} as SyncedBlocksSSRErrorMetadata,
				);
			}

			return;
		}

		// check if results match the requested block identifiers, if not,
		// it means the fetch was likely incomplete/failed,
		if (syncBlockReferences.length !== blockNodeIdentifiers.length) {
			// Find which block identifiers are missing from the results
			const returnedIdentifiers = new Set(
				syncBlockReferences.map(
					(ref) => `${ref.resourceId}:${ref.data?.blockInstanceId ?? 'unknown'}`,
				),
			);
			const missingIdentifiers = blockNodeIdentifiers.filter(
				(identifier) =>
					!returnedIdentifiers.has(`${identifier.resourceId}:${identifier.blockInstanceId}`),
			);

			ssrPrepareErrorsClient.submitError(
				new Error(
					`Synced block fetch returned incomplete results: ${missingIdentifiers.length} blocks missing`,
				),
				{
					code: SyncedBlocksSSRErrorCodeMap.FETCH_INCOMPLETE,
					contentId,
					cloudId,
					isBlog,
					identifiers: blockNodeIdentifiers,
					missingIdentifiers,
				} as SyncedBlocksSSRErrorMetadata,
			);
		}

		addToSSREditorNodeDataProvidersStore({ syncBlocks: syncBlockReferences });

		const syncedBlocksWithMedia = syncBlockReferences.filter((syncBlock) => {
			// Preload media data providers for synced blocks that contain media nodes
			const containedNodeTypes = syncBlockContainsNodes(syncBlock, ['media']);
			return containedNodeTypes.length > 0 && containedNodeTypes.includes('media');
		});

		const tasks: Promise<any>[] = [];
		syncedBlocksWithMedia.forEach((syncBlockInstance) => {
			tasks.push(preloadMediaTokensForSyncBlock(syncBlockInstance));
		});

		await Promise.allSettled(tasks);
	});
}

/**
 * Preload media tokens for synced blocks that contain media
 * Returns a map of resourceId -> media config for SSR
 */
export async function preloadMediaTokensForSyncBlock(
	syncBlock: SyncBlockInstance | undefined,
): Promise<Record<string, any> | null> {
	if (!syncBlock) {
		return null;
	}

	const { product, contentId } = parseResourceId(syncBlock.resourceId) || {};

	if (!product) {
		return {
			resourceId: syncBlock.resourceId,
			result: undefined,
		};
	}

	if (product === 'confluence-page') {
		if (!contentId) {
			return {
				resourceId: syncBlock.resourceId,
				result: undefined,
			};
		}

		const result = await preloadMediaToken(contentId);
		return {
			resourceId: syncBlock.resourceId,
			result,
		};
	}

	if (product === 'jira-work-item') {
		const jiraIssueAri = syncBlock.data?.sourceAri;
		if (!jiraIssueAri) {
			return {
				resourceId: syncBlock.resourceId,
				result: undefined,
			};
		}

		const result = await preloadJiraMediaToken(jiraIssueAri);
		return {
			resourceId: syncBlock.resourceId,
			result,
		};
	}

	return null;
}

/**
 * Fetches synced block references for a given content using batch API (bulk load).
 * Used when the ADF document is available for extracting block identifiers.
 * @param contentId - The ID of the content
 * @param cloudId - The cloud ID
 * @param isBlog - Whether the content is a blog post
 * @param blockNodeIdentifiers - Array of block node identifiers to fetch
 * @param measureSegment - Segment name for performance tracking
 * @param timeout - Maximum time in milliseconds to wait (default: 1500ms)
 * @returns Promise resolving to synced block instances or undefined on error/timeout
 */
export function fetchSyncBlockReferencesBulk(
	contentId: string,
	cloudId: string,
	isBlog: boolean,
	blockNodeIdentifiers: BlockNodeIdentifiers[],
	measureSegment: string = MEASURE_SEGMENT,
	timeout: number = expVal('platform_editor_sync_block_ssr_config', 'isEnabled', false)
		? DEFAULT_TIMEOUT_NEXT
		: DEFAULT_TIMEOUT,
): Promise<SyncBlockInstance[] | undefined> {
	return SSRMeasures.run(`${measureSegment}/fetch:SyncBlockReferences`, async () => {
		const pageAri = getConfluencePageAri({
			cloudId,
			pageId: contentId,
			pageType: isBlog ? 'blogpost' : 'page',
		});

		const timeoutError = new Error('timeout');

		let batchFetchDataConfig: BatchFetchConfig | undefined = undefined;
		if (expVal('platform_editor_sync_block_ssr_config', 'isEnabled', false)) {
			batchFetchDataConfig = {
				timeoutMs: expVal('platform_editor_sync_block_ssr_config', 'timeout', null) ?? timeout,
				isSSR: isSSR(),
				maxBatchSize:
					expVal('platform_editor_sync_block_ssr_config', 'maxBatchSize', null) ?? undefined,
			};
		}

		try {
			let results: SyncBlockInstance[] | undefined;
			if (expVal('platform_editor_sync_block_ssr_config', 'isEnabled', false)) {
				// We only passing the batchFetchDataConfig when the experiment is enabled,
				// however, batchFetchData is NOT gateed.
				results = await batchFetchData(
					cloudId,
					pageAri,
					blockNodeIdentifiers,
					batchFetchDataConfig,
				);
			} else {
				results = await Promise.race([
					batchFetchData(cloudId, pageAri, blockNodeIdentifiers),
					new Promise<undefined>((_resolve, reject) =>
						setTimeout(() => {
							reject(timeoutError);
						}, timeout),
					),
				]);
			}

			if (!results) {
				// No results returned - this could indicate a failure in the batch fetch operation
				// this would not be timeout, as it will throw a timeout error instead,
				// so we can attribute this to a fetch failure and log accordingly
				ssrPrepareErrorsClient.submitError(new Error('Synced blocks fetch returned no results'), {
					code: SyncedBlocksSSRErrorCodeMap.BATCH_FETCH_FAILED,
					contentId,
					cloudId,
					isBlog,
					identifiers: blockNodeIdentifiers,
				} as SyncedBlocksSSRErrorMetadata);

				return undefined;
			}

			// check if result is timeout error
			if (
				Array.isArray(results) &&
				results.length > 0 &&
				isSSR() && // we only want to apply this logic for SSR, as in client side we can allow for retries and better error handling.
				expVal('platform_editor_sync_block_ssr_config', 'isEnabled', false)
			) {
				const isTimeout = results.some((result) => SyncBlockError.Aborted === result.error?.type);

				if (isTimeout) {
					// This means the batch fetch operation timed out
					ssrPrepareErrorsClient.submitError(new Error('Synced blocks fetch operation timed out'), {
						code: SyncedBlocksSSRErrorCodeMap.FETCH_TIMEOUT,
						contentId,
						cloudId,
						isBlog,
						identifiers: blockNodeIdentifiers,
					} as SyncedBlocksSSRErrorMetadata);

					// we don't want to return timeout results,
					// as it might cause error on the initial page load.
					return undefined;
				}
			}

			return results;
		} catch (error) {
			// Network or API error
			const errorMessage = error instanceof Error ? error.message : String(error);
			const errorName = error instanceof Error ? error.name : undefined;

			ssrPrepareErrorsClient.submitError(
				new Error(
					`Batch fetch for synced blocks failed: unable to fetch ${blockNodeIdentifiers.length} blocks`,
				),
				{
					code: SyncedBlocksSSRErrorCodeMap.BATCH_FETCH_FAILED,
					contentId,
					cloudId,
					isBlog,
					identifiers: blockNodeIdentifiers,
					errorMessage,
					errorName,
				} as SyncedBlocksSSRErrorMetadata,
			);

			return undefined;
		}
	});
}
